import type { AutomationTriggerType } from '@adweb/shared';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { TriggerTypeBadge } from '../../components/ui/TriggerTypeBadge';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

import type { AutomationSummary } from './automation-summary';
import { AutomationActionButton } from './AutomationActionButton';
import { EditAutomationModal } from './EditAutomationModal';
import { type ParamField, RunAutomationModal } from './RunAutomationModal';

interface AutomationDetail {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  description: string | null;
  category: string | null;
  triggerType: AutomationTriggerType;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'RETIRED';
  formUrl: string | null;
  chatUrl: string | null;
  monitoredEmail: string | null;
  scheduleLabel: string | null;
  estimatedMinutesSavedPerRun: number | null;
  paramsSchema: { fields: ParamField[] } | null;
  createdAt: string;
}

async function fetchAutomation(id: string): Promise<AutomationDetail> {
  return (await apiClient.get<AutomationDetail>(`/automations/${id}`)).data;
}

// Champ copiable (id technique). Défini hors composant.
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
          {value}
        </code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" /> copié
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" /> copier
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value ?? '—'}</div>
    </div>
  );
}

export function AutomationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const [runOpen, setRunOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const query = useQuery({
    queryKey: ['automation', id],
    queryFn: () => fetchAutomation(id!),
    enabled: !!id,
  });

  if (query.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Chargement…</div>;
  }
  if (!query.data) {
    return <div className="p-8 text-sm text-slate-500">Automatisation introuvable.</div>;
  }
  const a = query.data;
  // Action principale selon le type (Exécuter, formulaire, chat, ou mode automatique).
  // L'API revérifie le droit de déclenchement ; un run est enregistré en « Manuel ».
  const summary: AutomationSummary = { ...a, organization: undefined };

  return (
    <>
      <PageHeader
        helpTopic="lancer"
        title={a.name}
        subtitle={a.slug}
        actions={
          <>
            <AutomationActionButton automation={summary} onRun={() => setRunOpen(true)} size="md" />
            {isPlatform && (
              <Button variant="inverse" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Modifier
              </Button>
            )}
            <StatusBadge status={a.status} />
          </>
        }
      />
      <EditAutomationModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        automationId={editOpen ? a.id : null}
      />
      <RunAutomationModal
        open={runOpen}
        onClose={() => setRunOpen(false)}
        automationId={a.id}
        automationName={a.name}
        fields={a.paramsSchema?.fields ?? []}
      />
      <section className="grid grid-cols-1 gap-6 px-4 sm:px-6 lg:px-8 py-6 lg:grid-cols-2">
        <div className="surface p-5">
          <h2 className="mb-4 text-sm font-medium text-slate-700">Informations</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Type de déclenchement"
              value={<TriggerTypeBadge type={a.triggerType} />}
            />
            <Field label="Catégorie" value={a.category ?? '—'} />
            <Field
              label="Temps gagné / exécution"
              value={
                a.estimatedMinutesSavedPerRun != null ? `${a.estimatedMinutesSavedPerRun} min` : '—'
              }
            />
            <Field label="Créée le" value={new Date(a.createdAt).toLocaleDateString('fr-FR')} />
            {a.triggerType === 'FORM' && a.formUrl && (
              <div className="col-span-2">
                <Field
                  label="URL du formulaire"
                  value={
                    <a
                      href={a.formUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand hover:underline"
                    >
                      {a.formUrl}
                    </a>
                  }
                />
              </div>
            )}
            {a.triggerType === 'SCHEDULED' && (
              <div className="col-span-2">
                <Field label="Planification" value={a.scheduleLabel ?? '—'} />
              </div>
            )}
            {a.triggerType === 'CHAT' && a.chatUrl && (
              <div className="col-span-2">
                <Field
                  label="URL du widget de chat"
                  value={
                    <a
                      href={a.chatUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand hover:underline"
                    >
                      {a.chatUrl}
                    </a>
                  }
                />
              </div>
            )}
            {a.triggerType === 'EMAIL' && a.monitoredEmail && (
              <div className="col-span-2">
                <Field
                  label="Adresse e-mail surveillée"
                  value={
                    <a href={`mailto:${a.monitoredEmail}`} className="text-brand hover:underline">
                      {a.monitoredEmail}
                    </a>
                  }
                />
              </div>
            )}
          </div>
          {a.description && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Description</div>
              <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{a.description}</p>
            </div>
          )}
        </div>

        {/* Section technique pour câbler n8n — réservée aux rôles Adweb-Systèmes */}
        {isPlatform && (
          <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-5">
            <h2 className="mb-1 text-sm font-medium text-slate-700">Intégration n8n</h2>
            <p className="mb-4 text-xs text-slate-500">
              Identifiants à utiliser dans le workflow n8n pour faire remonter les exécutions vers{' '}
              <code className="rounded bg-slate-100 px-1 text-[11px]">/ingest/events</code>.
            </p>
            <div className="space-y-3">
              <CopyRow label="automationId" value={a.id} />
              <CopyRow label="organizationId" value={a.organizationId} />
            </div>
            <p className="mt-4 text-[11px] text-slate-500">
              Le secret d'ingestion (<code className="text-[11px]">INGEST_HMAC_SECRET</code>) et
              l'URL d'ingestion sont des variables globales — pas besoin de les changer par
              automatisation.
            </p>
          </div>
        )}
      </section>

      {/* Discussion in-app avec le chatbot — uniquement pour les automatisations CHAT. */}
      {a.triggerType === 'CHAT' && a.chatUrl && (
        <section className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="overflow-x-auto surface">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div>
                <h2 className="text-sm font-medium text-slate-700">Tester le chatbot</h2>
                <p className="text-xs text-slate-500">
                  Discussion directe avec le widget n8n — utile pour valider une mise en place ou
                  faire un test rapide sans quitter le dashboard.
                </p>
              </div>
              <a
                href={a.chatUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-brand hover:underline"
              >
                Ouvrir en plein écran ↗
              </a>
            </div>
            <iframe
              src={a.chatUrl}
              title={`Chat : ${a.name}`}
              className="h-[640px] w-full bg-slate-50"
              allow="microphone; clipboard-write"
            />
            <p className="border-t border-slate-200 bg-slate-50 px-5 py-2 text-[11px] text-slate-500">
              Le widget ne s'affiche pas ? Ton instance n8n bloque peut-être l'embed iframe via
              <code className="mx-1 rounded bg-slate-200 px-1 text-[10px]">X-Frame-Options</code>/
              CSP. Utilise « Ouvrir en plein écran » en attendant.
            </p>
          </div>
        </section>
      )}
    </>
  );
}
